import { VisitsByHourOfDayData } from '@components/Dashboard/CampaignAnalytics/slice/schema';
import { CA_LIGHT_BLUE_COLOR } from '@utils/colorGenerator';
import { AXIS_COLOR, baseChart } from './baseChart';
import { SUBHEADER_FONT_SIZE } from '@components/shared/Layout/ReportSectionCardHeader';
import Highcharts from 'highcharts';

const START_HOUR = 6;
const END_HOUR = 23;

const dayParts = [
  {
    name: 'Early Morning',
    label: 'Before 10am',
    start: START_HOUR,
    end: 9,
  },
  {
    name: 'Late Morning',
    label: '10am - 12pm',
    start: 10,
    end: 11,
  },
  {
    name: 'Early Afternoon',
    label: '12pm - 2pm',
    start: 12,
    end: 13,
  },
  {
    name: 'Late Afternoon',
    label: '2pm - 5pm',
    start: 14,
    end: 16,
  },
  {
    name: 'Early Evening',
    label: '5pm - 8pm',
    start: 17,
    end: 19,
  },
  {
    name: 'Night',
    label: 'After 8pm',
    start: 20,
    end: END_HOUR,
  },
];

const dayPartFromHour = (hour: number) =>
  dayParts.find(({ start, end }) => hour >= start && hour <= end);

export const visitsByHourOfDayDataToSeriesData = (
  data: VisitsByHourOfDayData[]
) => {
  const visitsByDayPart = data.reduce(
    (acc, data) => {
      const dayPart = dayPartFromHour(data.hour);
      if (!dayPart) return acc;

      const value = acc[dayPart.name] ?? 0;
      acc[dayPart.name] = value + data.visits;

      return acc;
    },
    {} as Record<string, number>
  );

  const visitsByDayPartValues = Object.values(visitsByDayPart);
  const averageVisitsByDayPart =
    visitsByDayPartValues.reduce((acc, visits) => acc + visits, 0) /
    visitsByDayPartValues.length;

  return dayParts.map(dayPart => ({
    name: dayPart.name,
    y: ((visitsByDayPart[dayPart.name] ?? 0) / averageVisitsByDayPart) * 100,
  }));
};

export const buildChartOptions = (
  data: VisitsByHourOfDayData[],
  small?: boolean
): Highcharts.Options => {
  return {
    ...baseChart,

    chart: {
      type: 'column',
      marginLeft: 50,
      marginRight: 50,
    },

    plotOptions: {
      column: {
        borderRadius: 5,
      },
    },

    series: [
      {
        type: 'column',
        data: visitsByHourOfDayDataToSeriesData(data),
        color: CA_LIGHT_BLUE_COLOR,
        dataLabels: {
          enabled: false,
        },
      },
    ],

    tooltip: {
      enabled: true,
      formatter() {
        const dayPart = dayParts[this.x];
        return `${dayPart?.label}: ${this.y?.toFixed(0)}`;
      },
    },

    xAxis: {
      type: 'category',
      startOnTick: true,
      endOnTick: true,
      tickInterval: 1,
      tickColor: AXIS_COLOR,
      minPadding: 0,
      maxPadding: 0,
      labels: {
        enabled: true,
        style: {
          fontSize: SUBHEADER_FONT_SIZE,
        },
      },
      tickLength: 0,
      title: {
        text: 'Time of Day',
      },
    },

    yAxis: {
      lineColor: AXIS_COLOR,
      lineWidth: 1,
      maxPadding: small ? 0.4 : 0.1,
      gridLineWidth: 0,
      min: 0,
      tickColor: AXIS_COLOR,
      tickWidth: 1,
      tickLength: 5,
      tickInterval: 50,
      labels: {
        enabled: true,
        style: {
          fontSize: SUBHEADER_FONT_SIZE,
        },
      },
      title: {
        text: 'Hourly Visit Index (Average = 100)',
      },
    },
  };
};
